import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios';
import {
  CustomerData,
  CustomerCreateData,
  CustomerUpdateData,
  FetchCustomersParams,
} from '../types/customerTypes';

// Utility function to ensure consistent response handling
const handleApiResponse = (response: any) => {
  if (response.data?.success === false) {
    throw new Error(response.data?.message || 'API request failed');
  }
  return response.data;
};

// Utility function for base64 encoding
const toBase64 = (str: string) => btoa(str);

// Fetch all customers with pagination
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params: FetchCustomersParams = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search } = params;
      const response = await axiosInstance.get('/v1/admin/client', {
        params: { page, limit, ...(search ? { search } : {}) },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch customers'
      );
    }
  }
);

// Fetch a single customer by ID
export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (id: number | string, { rejectWithValue }) => {
    try {
      // Use the ID directly without transformation
      const response = await axiosInstance.get(`/v1/admin/client/${id}`);
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch customer'
      );
    }
  }
);

// Create a new customer
export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData: CustomerCreateData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('/v1/admin/client', customerData);
      
      // After successfully creating a customer, refresh the customer list with a higher limit
      // to ensure we get all customers including the newly created one
      await dispatch(fetchCustomers({ page: 1, limit: 500 }));
      
      return handleApiResponse(response);
    } catch (error: any) {
      // Return the complete error object from the API response
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Failed to create customer' });
    }
  }
);

// Update an existing customer
export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, data }: { id: string | number; data: CustomerUpdateData }, { rejectWithValue }) => {
    try {
      // Use the customer ID directly without transformation
      const response = await axiosInstance.put(`/v1/admin/client/${id}`, data);
      return handleApiResponse(response);
    } catch (error: any) {
      // Return the complete error response if available
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({
        success: false,
        message: error.message || 'Failed to update customer',
        errors: [],
      });
    }
  }
);

// Search customers
export const searchCustomers = createAsyncThunk(
  'customers/searchCustomers',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/v1/admin/client', {
        params: { search: query },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to search customers'
      );
    }
  }
);

// Fetch projects for a specific customer
export const fetchCustomerProjects = createAsyncThunk(
  'customers/fetchCustomerProjects',
  async (
    { customerId, customerName, page = 1, limit = 10 }: { customerId: string; customerName: string; page?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get('/v1/admin/project', {
        params: {
          customerName: customerName,
          status: 'DELIVERED', // Only fetch delivered projects
          page,
          limit,
        },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch customer projects'
      );
    }
  }
);
